/**
 * Color constants for attachment UI components.
 * Lightened badge colors per GitLab #608 requirements.
 */

/**
 * Lighten a hex color by a percentage using HSL conversion.
 * @param hex - Hex color string (e.g., "#6c757d")
 * @param percent - Percentage to lighten (e.g., 25 for 25% lighter)
 * @returns Lightened hex color string
 */
export const lightenColor = (hex: string, percent: number): string => {
  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Convert hex to RGB
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  // Convert RGB to HSL
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  let h = 0;
  let s = 0;
  let l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case rNorm:
        h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6;
        break;
      case gNorm:
        h = ((bNorm - rNorm) / d + 2) / 6;
        break;
      case bNorm:
        h = ((rNorm - gNorm) / d + 4) / 6;
        break;
    }
  }

  // Increase lightness by percentage
  l = Math.min(1, l + percent / 100);

  // Convert HSL back to RGB
  const hueToRgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let rOut: number, gOut: number, bOut: number;

  if (s === 0) {
    rOut = gOut = bOut = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    rOut = hueToRgb(p, q, h + 1 / 3);
    gOut = hueToRgb(p, q, h);
    bOut = hueToRgb(p, q, h - 1 / 3);
  }

  // Convert back to hex
  const toHex = (n: number) => {
    const component = Math.round(n * 255).toString(16);
    return component.length === 1 ? '0' + component : component;
  };

  return `#${toHex(rOut)}${toHex(gOut)}${toHex(bOut)}`;
};

/**
 * Original gray color used in badges/headers
 */
const ORIGINAL_GRAY = '#6c757d';

/**
 * Attachment color palette
 */
export const ATTACHMENT_COLORS = {
  // Stage count badge in grouped view only (flat view section labels are plain text, no badge)
  SECTION_BADGE_COLOR: lightenColor(ORIGINAL_GRAY, 25), // #8a939d

  // Retained for any consumers that may still reference these; grouped view headers
  // no longer use background colors — see STAGE_HEADER_TEXT_COLOR / SUBSTAGE_HEADER_TEXT_COLOR
  STAGE_HEADER_COLOR: lightenColor(ORIGINAL_GRAY, 25), // #8a939d — legacy, not used for backgrounds
  SUBSTAGE_HEADER_COLOR: ORIGINAL_GRAY, // #6c757d — legacy, not used for backgrounds

  // Card borders
  CARD_BORDER_COLOR: '#e0e0e0',

  // Background colors
  CARD_BACKGROUND_COLOR: '#fff',
  TABLE_HEADER_BACKGROUND_COLOR: '#f5f5f5',

  // Text colors
  FILENAME_COLOR: '#000',
  CAPTION_COLOR: '#666',
  METADATA_COLOR: '#999',

  // Grouped view header text colors (no background — darker for readability)
  STAGE_HEADER_TEXT_COLOR: '#1a1a1a',    // Near-black, matches app's primary text
  SUBSTAGE_HEADER_TEXT_COLOR: '#444444', // Dark gray, lighter than stage for hierarchy
  STAGE_HEADER_BORDER_COLOR: '#f0f0f0', // Divider under stage headers, matches SubsectionForm header style

  // Icon colors
  PDF_ICON_COLOR: '#d32f2f',
  IMAGE_ICON_COLOR: '#1976d2',
  VIDEO_ICON_COLOR: '#7b1fa2',
  AUDIO_ICON_COLOR: '#f57c00',
  FILE_ICON_COLOR: '#616161',

  // Action button colors
  ACTION_BUTTON_COLOR: '#1976d2',
  DELETE_BUTTON_COLOR: '#d32f2f',

  // State colors
  DELETING_OPACITY: 0.5,
  OFFLINE_BADGE_COLOR: '#f57c00',
};

// Convenience re-export for the count badge color used in grouped stage headers
export const SECTION_BADGE_COLOR = ATTACHMENT_COLORS.SECTION_BADGE_COLOR;
